import type { CardImportantNumber } from "@kontextwerk/web-sdk/core/layout/card"
import type { DistrictStatistics } from "../../../../../../../api/kehrblatt/district/statistics"

export type DistrictStatisticsCard = {
  id: string
  title: string
  description: string
  value: string
  importantNumbers: CardImportantNumber[]
}

const numberFormatter = new Intl.NumberFormat("de-DE")
const percentageFormatter = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 })

export const formatReachability = (reachable: number, total: number) =>
  `${percentageFormatter.format(total > 0 ? (reachable / total) * 100 : 0)} %`

export const buildDistrictStatisticsCards = (
  statistics: DistrictStatistics,
): DistrictStatisticsCard[] => {
  const { properties, contact_points: contacts, customers } = statistics
  const structureNumbers: CardImportantNumber[] = [
    {
      key: "single-family",
      label: "EFH",
      value: properties.single_family,
      description: "Gebäudeeinheit ohne separate Wohneinheiten.",
    },
    {
      key: "multi-family",
      label: "MFH",
      value: properties.multi_family,
      description: "Gebäudeeinheit mit separaten Wohneinheiten.",
    },
  ]

  if (properties.unknown > 0) {
    structureNumbers.push({
      key: "unknown",
      label: "Unklar",
      value: properties.unknown,
      description: "Liegenschaften ohne aktive Gebäudezuordnung.",
    })
  }

  return [
    {
      id: "structure",
      title: "Kehrbezirksstruktur",
      description: "Aktive Liegenschaften",
      value: numberFormatter.format(properties.total),
      importantNumbers: structureNumbers,
    },
    {
      id: "contacts",
      title: "Digitale Kontaktdaten",
      description: "Gespeicherte aktive Kontaktpunkte",
      value: numberFormatter.format(contacts.email + contacts.mobile + contacts.fixed_line),
      importantNumbers: [
        {
          key: "email",
          label: "E-Mail",
          value: contacts.email,
          description:
            "Aktive gespeicherte E-Mail-Adressen. Kontaktverbote ändern die Bestandszahl nicht; gleiche Werte bei verschiedenen Datensätzen können mehrfach zählen.",
        },
        {
          key: "mobile",
          label: "Mobil",
          value: contacts.mobile,
          description:
            "Aktive gespeicherte Mobilfunknummern. Kontaktverbote ändern die Bestandszahl nicht; gleiche Werte bei verschiedenen Datensätzen können mehrfach zählen.",
        },
        {
          key: "fixed-line",
          label: "Festnetz",
          value: contacts.fixed_line,
          description:
            "Aktive gespeicherte Festnetznummern. Kontaktverbote ändern die Bestandszahl nicht; gleiche Werte bei verschiedenen Datensätzen können mehrfach zählen.",
        },
      ],
    },
    {
      id: "customers",
      title: "Ansprechpartner",
      description: "Erlaubte Kontaktwege je Ansprechpartner",
      value: numberFormatter.format(customers.total),
      importantNumbers: [
        {
          key: "email",
          label: "E-Mail",
          value: formatReachability(customers.email_reachable, customers.total),
          description:
            "Anteil aktiver Ansprechpartner mit mindestens einer aktiven, erlaubten E-Mail-Adresse. Jeder Ansprechpartner zählt einmal. Die Kanalanteile können sich überschneiden.",
        },
        {
          key: "message",
          label: "Nachricht",
          value: formatReachability(customers.message_reachable, customers.total),
          description:
            "Nachricht = SMS oder WhatsApp: Anteil aktiver Ansprechpartner mit mindestens einer aktiven Mobilnummer und erlaubtem Nachrichtenkanal. Jeder Ansprechpartner zählt einmal. Dies belegt weder Zustellung noch eine geprüfte WhatsApp-Verfügbarkeit.",
        },
        {
          key: "phone",
          label: "Telefon",
          value: formatReachability(customers.phone_reachable, customers.total),
          description:
            "Anteil aktiver Ansprechpartner mit mindestens einer aktiven Mobilfunk- oder Festnetznummer und erlaubtem Telefonkanal. Jeder Ansprechpartner zählt einmal. Die Kanalanteile können sich überschneiden.",
        },
      ],
    },
  ]
}
